"use client";

import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";

interface YouTubeEmbedProps {
  videoId: string;
  title?: string;
  autoplay?: boolean;
  showControls?: boolean;
  privacy?: boolean;
  aspectRatio?: string;
}

export const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({
  videoId,
  title = "YouTube video player",
  autoplay = false,
  showControls = true,
  privacy = true,
  aspectRatio = "16/9",
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const baseUrl = privacy
    ? "https://www.youtube-nocookie.com/embed/"
    : "https://www.youtube.com/embed/";

  const params = new URLSearchParams({
    ...(autoplay && { autoplay: "1" }),
    ...(showControls && { controls: "1" }),
    rel: "0",
    modestbranding: "1",
  });

  const embedUrl = `${baseUrl}${videoId}?${params.toString()}`;
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        paddingBottom: `calc(100% / (${aspectRatio}))`,
        overflow: "hidden",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      {!isLoaded && (
        <Box
          onClick={() => setIsLoaded(true)}
          sx={{
            position: "absolute",
            inset: 0,
            cursor: "pointer",
            transition: "all 0.3s",
            "&:hover .play-button": {
              transform: "scale(1.1)",
              backgroundColor: "#c62828",
            },
            "&:hover .overlay": {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <Box
            component="img"
            src={thumbnailUrl}
            alt={title}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />

          <Box
            className="overlay"
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              transition: "all 0.3s",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              className="play-button"
              sx={{
                backgroundColor: "#d32f2f",
                borderRadius: "50%",
                width: 80,
                height: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                transition: "all 0.3s",
              }}
            >
              <PlayArrow sx={{ fontSize: 48, color: "white", ml: 0.5 }} />
            </Box>
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              background:
                "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "white", fontWeight: 500 }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
      )}

      {isLoaded && (
        <Box
          component="iframe"
          src={embedUrl}
          title={title}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      )}
    </Box>
  );
};

const CaseStudyVideoShowcase: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: 8 }}>
      {/* Section Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "2rem", md: "2.5rem" },
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            mb: 2,
          }}
        >
          See Our Work in Action
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 700, mx: "auto", lineHeight: 1.6 }}
        >
          Watch how we&apos;ve transformed businesses through innovative
          automation and web development solutions
        </Typography>
      </Box>

      {/* Featured Video */}
      <Box sx={{ mb: 6 }}>
        <Card
          sx={{
            p: 3,
            boxShadow: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Chip
              label="Featured Project"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: "white",
                fontWeight: 600,
              }}
            />
          </Box>

          <YouTubeEmbed
            videoId="MW9XHMdZqBw"
            title="I Automated My Agency’s Onboarding (Save 10+ Hours/Week)
"
            privacy={true}
          />

          <CardContent sx={{ px: 0, pb: 0, pt: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              I Automated My Agency’s Onboarding (Save 10+ Hours/Week)
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Watch how we transformed a chaotic hiring process into a seamless, automated workflow using Make.com and Groq AI.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label="E-commerce"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              />
              <Chip
                label="Process Automation"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                }}
              />
              <Chip
                label="CRM Integration"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Video Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              p: 2.5,
              height: "100%",
              transition: "all 0.3s",
              boxShadow: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              "&:hover": {
                boxShadow: 4,
              },
            }}
          >
            <YouTubeEmbed
              videoId="DRtQLG1sqhA"
              title="Automate Your WhatsApp Sales in 60 Seconds | LinXar Ops"
              privacy={true}
            />
            <CardContent sx={{ px: 0, pb: 0, pt: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Automate Your WhatsApp Sales in 60 Seconds | LinXar Ops
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Is your WhatsApp chaotic? Are you losing clients because you can&apos;t reply fast enough? Stop chatting and start scaling with LinXar Ops.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label="SaaS"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    fontSize: "0.75rem",
                  }}
                />
                <Chip
                  label="Node.js"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              p: 2.5,
              height: "100%",
              transition: "all 0.3s",
              boxShadow: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              "&:hover": {
                boxShadow: 4,
              },
            }}
          >
            <YouTubeEmbed
              videoId="oouJZHMGgeQ"
              title="AI Chatbot Implementation Case Study"
              privacy={true}
            />
            <CardContent sx={{ px: 0, pb: 0, pt: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                AI-Powered Customer Support
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Implementing an intelligent chatbot that reduced support tickets
                by 60% while improving customer satisfaction.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label="AI Integration"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    fontSize: "0.75rem",
                  }}
                />
                <Chip
                  label="Automation"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Client Testimonial */}
      {/* <Card
        sx={{
          p: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          mb: 6,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography variant="h3" sx={{ color: "white", fontWeight: 700 }}>
              &quot;
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontStyle: "italic", mb: 2, lineHeight: 1.6 }}
            >
              &quot;LinXar Labs transformed our entire business operation. The
              automation solutions they implemented saved us over 20 hours per
              week and the ROI was visible within the first month. Highly
              recommended!&quot;
            </Typography>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Sarah Mitchell
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CEO, TechFlow Solutions
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card> */}

      {/* CTA Section */}
      <Card
        sx={{
          p: 4,
          textAlign: "center",
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Ready to Transform Your Business?
        </Typography>
        <Typography sx={{ mb: 3, opacity: 0.9, maxWidth: 600, mx: "auto" }}>
          Let&apos;s create a custom solution that drives real results for your
          company
        </Typography>
        <Button
          variant="contained"
          size="large"
          href="/contact"
          sx={{
            backgroundColor: "white",
            color: theme.palette.primary.main,
            fontWeight: 600,
            px: 4,
            "&:hover": {
              backgroundColor: alpha("#fff", 0.9),
              transform: "translateY(-2px)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            },
            transition: "all 0.3s",
          }}
        >
          Schedule Your Free Consultation
        </Button>
      </Card>
    </Box>
  );
};

export default CaseStudyVideoShowcase;
